export const MAX_TRANSACTION_PROOF_IMAGES = 5;
export const MAX_TRANSACTION_PROOF_IMAGE_BYTES = 5 * 1024 * 1024;
export const PROOF_IMAGE_ACCEPT = "image/*";

const IMAGE_FILE_EXTENSION_PATTERN = /\.(avif|bmp|gif|jpe?g|png|tiff?|webp)$/i;

const getLocalProofImageId = (prefix = "proof") => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return `${prefix}-${crypto.randomUUID()}`;
  }
  return `${prefix}-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

export const normalizeProofImageUrls = (value) => {
  const rawUrls = [];

  if (typeof value === "string") {
    const normalizedValue = value.trim();
    if (normalizedValue) {
      rawUrls.push(normalizedValue);
    }
  } else if (Array.isArray(value)) {
    value.forEach((item) => {
      const normalizedValue = String(item || "").trim();
      if (normalizedValue) {
        rawUrls.push(normalizedValue);
      }
    });
  }

  return rawUrls.filter((url, index) => rawUrls.indexOf(url) === index).slice(0, MAX_TRANSACTION_PROOF_IMAGES);
};

const dedupeFiles = (files) => {
  const seen = new Set();

  return files.filter((file) => {
    const key = [
      String(file?.name || ""),
      Number(file?.size || 0),
      Number(file?.lastModified || 0),
    ].join(":");

    if (seen.has(key)) {
      return false;
    }

    seen.add(key);
    return true;
  });
};

const collectFilesFromCollection = (collection) => {
  if (!collection) {
    return [];
  }

  return Array.from(collection).filter(Boolean);
};

const collectFilesFromItems = (items) => {
  if (!items) {
    return [];
  }

  const files = [];
  Array.from(items).forEach((item) => {
    if (item?.kind === "file" && typeof item.getAsFile === "function") {
      const file = item.getAsFile();
      if (file) {
        files.push(file);
      }
    }
  });
  return files;
};

export const isLikelyProofImageFile = (file) => {
  if (!file) {
    return false;
  }

  const mimeType = String(file.type || "").toLowerCase();
  if (mimeType.startsWith("image/")) {
    return true;
  }

  const fileName = String(file.name || "").trim();
  return IMAGE_FILE_EXTENSION_PATTERN.test(fileName);
};

export const collectProofImageFiles = (source) => {
  if (!source) {
    return [];
  }

  if (typeof File !== "undefined" && source instanceof File) {
    return [source];
  }

  const fileCollections = [
    source,
    source?.files,
    source?.target?.files,
    source?.dataTransfer?.files,
    source?.clipboardData?.files,
  ];

  for (const collection of fileCollections) {
    const files = collectFilesFromCollection(collection);
    if (files.length > 0) {
      return dedupeFiles(files);
    }
  }

  const itemCollections = [source?.items, source?.dataTransfer?.items, source?.clipboardData?.items];
  for (const items of itemCollections) {
    const files = collectFilesFromItems(items);
    if (files.length > 0) {
      return dedupeFiles(files);
    }
  }

  return [];
};

export const getTransactionProofImageUrls = (transaction) => {
  const listValue = transaction?.foto_bukti_list;
  if (Array.isArray(listValue) && listValue.length > 0) {
    return normalizeProofImageUrls(listValue);
  }
  return normalizeProofImageUrls(transaction?.foto_bukti);
};

export const buildProofImageSrc = (url) => {
  const normalizedUrl = String(url || "").trim();
  if (!normalizedUrl) {
    return "";
  }
  if (/^(https?:|data:|blob:)/i.test(normalizedUrl)) {
    return normalizedUrl;
  }
  const backendUrl = String(process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
  if (!backendUrl) {
    return normalizedUrl;
  }
  return normalizedUrl.startsWith("/") ? `${backendUrl}${normalizedUrl}` : `${backendUrl}/${normalizedUrl}`;
};

export const readFileAsDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(new Error("Gagal membaca file gambar"));
    reader.readAsDataURL(file);
  });

export const createLocalProofImageEntries = async (files, prefix = "proof") => {
  const fileList = Array.from(files || []);
  return Promise.all(
    fileList.map(async (file) => ({
      id: getLocalProofImageId(prefix),
      file,
      previewUrl: await readFileAsDataUrl(file),
    }))
  );
};
